import React from 'react';

import { translate } from '~l10n/client';
import { useAtom } from 'jotai/index';

import { ConfirmTransform, Dialog } from '~shared/components/Dialog';
import { toast } from '~shared/components/Toast';
import { useDisableWorkspaceCode } from '~teacher/hook/workspaces/code';

import { WorkspaceRole } from '../../../../../api/workspaces/getWorkspaceMembers';
import { workspaceInviteDialogAtom } from '../atoms/atoms';

export type DisableInviteConfirmationProps = {
  workspaceId: string;
  isOpen: boolean;
  handleClose: () => void;
};

export function DisableInviteConfirmation({
  workspaceId,
  isOpen,
  handleClose,
}: DisableInviteConfirmationProps) {
  const [{ currentMemberLinkType }, dispatch] = useAtom(
    workspaceInviteDialogAtom
  );

  const onClose = () => dispatch({ type: 'Close' });

  const { isLoading, disableInviteCode } = useDisableInviteCode({
    workspaceId: workspaceId,
    workspaceRole: currentMemberLinkType,
    handleOverlayClose: onClose,
  });

  return (
    <Dialog
      onClose={handleClose}
      padding={'confirmation'}
      id="invite-workspace-dialog"
      open={isOpen}
      title={''}
      closeLabel={translate('Cancel')}
    >
      <ConfirmTransform
        isLoading={isLoading}
        title={translate('Disable Invite')}
        onClose={onClose}
        confirm={translate('Disable Invite')}
        action={disableInviteCode}
        description={translate(
          'People will no longer be able to use the current invite.'
        )}
      />
    </Dialog>
  );
}

interface UseDisableInviteCodeParams {
  workspaceId: string;
  workspaceRole: WorkspaceRole;
  handleOverlayClose: () => void;
}

function useDisableInviteCode({
  workspaceId,
  workspaceRole,
  handleOverlayClose,
}: UseDisableInviteCodeParams) {
  const [isLoading, setIsLoading] = React.useState(false);
  const disableWorkspaceCode = useDisableWorkspaceCode();

  const disableInviteCode = () => {
    setIsLoading(true);
    disableWorkspaceCode.mutate(
      {
        workspaceId: workspaceId,
        workspaceRole: workspaceRole,
      },
      {
        onSuccess: () => handleOverlayClose(),
        onSettled: () => {
          setIsLoading(false);
        },
        onError: () => toast.critical(translate('An error has occurred')),
      }
    );
  };
  return { isLoading, disableInviteCode };
}
